import {
  resolveMillingVariant,
  millingItemDimensions
} from "./millingVariants";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  mainMilling,
  millingAsset,
  millingImage,
  millingImageSize
} from "./millingAssets";
import { createMillingDisplay } from "./millingGeometry";

const EMPTY = [];
export default function useMillingDisplay() {
  const catalog = useSelector((state) => state.millings) || EMPTY;
  const [show3D, setShow3D] = useState(false);
  const [status, setStatus] = useState("");
  const records = useRef(new Set());
  const assets = useRef(new Map());
  const cached = useCallback((key, load) => {
    if (!assets.current.has(key)) {
      const promise = load().catch((error) => {
        assets.current.delete(key);
        throw error;
      });
      assets.current.set(key, promise);
    }
    return assets.current.get(key);
  }, []);
  const config = useRef({ catalog, show3D });
  config.current = { catalog, show3D };
  const mounted = useRef(true);
  const report = useCallback(() => {
    if (!mounted.current) return;
    const all = [...records.current];
    const loading = all.filter((r) => r.loading).length;
    const error = all.find((r) => r.error)?.error;
    setStatus(
      loading
        ? `${config.current.show3D ? "3D-marás" : "Maráskép"} betöltése (${loading} elem)…`
        : error || ""
    );
  }, []);
  const update = useCallback(
    async (record) => {
      const version = ++record.version;
      const { catalog: currentCatalog, show3D: detailed } = config.current;
      const dimensions = millingItemDimensions(record.item, record.dimensions);
      const milling = resolveMillingVariant(
        mainMilling(record.item, currentCatalog),
        record.item.millingVariantId,
        dimensions.width,
        dimensions.height
      );
      const stretch = {
        stretchX: milling?.stretchX,
        stretchY: milling?.stretchY,
        repeatX: milling?.repeatX,
        repeatY: milling?.repeatY
      };
      record.display.reset();
      record.error = "";
      if (!milling?.dxfKey && !milling?.imageKey) {
        record.loading = false;
        report();
        return;
      }
      record.loading = true;
      report();
      const current = () =>
        records.current.has(record) &&
        record.version === version &&
        !record.mesh.isDisposed();
      try {
        if (milling.imageKey) {
          try {
            const flat = await cached(`image:${milling.imageKey}`, () =>
              millingImage(milling.imageKey)
            );
            const size = await cached(
              `size:${milling.imageKey}:${milling.dxfKey}:${milling.imageWidthMm}:${milling.imageHeightMm}`,
              () => millingImageSize(milling)
            );
            if (!current()) return;
            record.display.flat(
              { ...flat, size, ...stretch },
              milling.imageKey
            );
          } catch (error) {
            if (!detailed) throw error;
          }
        }
        if (!current()) return;
        if (detailed) {
          if (!milling.dxfKey)
            throw new Error("Ehhez a maráshoz még nincs DXF.");
          const solid = await cached(`3d:${milling.dxfKey}`, () =>
            millingAsset(milling.dxfKey, "3d")
          );
          if (!current()) return;
          record.display.solid({ ...solid, ...stretch });
        }
      } catch (error) {
        if (current()) {
          record.error = `${milling.name || "Marás"}: ${error.message || "Nem sikerült betölteni."}`;
          if (milling.dxfKey && milling.imageKey) {
            try {
              const fallback = await cached(`image:${milling.imageKey}`, () =>
                millingImage(milling.imageKey)
              );
              const size = await millingImageSize(milling);
              if (current())
                record.display.flat(
                  { ...fallback, size, ...stretch },
                  milling.imageKey
                );
            } catch (_) {
              /* Keep the original, visible loading error. */
            }
          }
        }
      } finally {
        if (current()) {
          record.loading = false;
          report();
        }
      }
    },
    [report, cached]
  );
  const attach = useCallback(
    (mesh, item, dimensions) => {
      const record = {
        mesh,
        item,
        dimensions,
        display: createMillingDisplay(mesh, dimensions),
        version: 0
      };
      records.current.add(record);
      mesh.onDisposeObservable.addOnce(() => {
        records.current.delete(record);
        record.version++;
        record.display.dispose();
        report();
      });
      update(record);
    },
    [update, report]
  );
  useEffect(() => {
    records.current.forEach(update);
  }, [catalog, show3D, update]);
  useEffect(() => {
    mounted.current = true;
    const current = records.current;
    return () => {
      mounted.current = false;
      current.forEach((r) => {
        r.version++;
        r.display.dispose();
      });
      current.clear();
    };
  }, []);
  return {
    attach,
    show3D,
    toggle: () => setShow3D((value) => !value),
    status,
    retry: () => records.current.forEach(update)
  };
}
